import { useSuspenseQuery } from '@tanstack/react-query'
import { type ReactNode, createContext, useEffect, useRef } from 'react'

import { usersQueryKeys } from '@/features/users/query-keys'
import { type UserMeExEntity } from '@/libs/api'

import { fetchMe } from '../api'
import { useReloadIfAuthUserChanged } from '../hooks/use-reload-if-auth-user-changed'

export type AuthContextType = {
  isAuthenticated: boolean
  user: UserMeExEntity
}

export const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data } = useSuspenseQuery({
    queryKey: usersQueryKeys.me(),
    queryFn: fetchMe,
    refetchOnWindowFocus: true,
    retry: false
  })
  useReloadIfAuthUserChanged(data)

  // 認証済みセッションを一度でも掴んだことがあるかを追跡する。
  // refetchOnWindowFocus で 401 を踏み data が null へ遷移した場合に、
  // 初回 null（未ログイン）と区別するために必要。
  // 初回マウント時点の data 値で初期化し、以降は useEffect で commit 後にのみ
  // 更新する（StrictMode / Concurrent Rendering で discard されたレンダーの
  // 副作用が残らないようにするため）。
  const wasAuthenticatedRef = useRef(!!data)
  useEffect(() => {
    if (data) wasAuthenticatedRef.current = true
  }, [data])

  // 認証済みだったセッションが refetchOnWindowFocus で null に切り替わったケース。
  // useReloadIfAuthUserChanged が location.reload() を呼んでいるが、
  // リロード成立までの 1 描画分で useAuth().user が null を引いて crash するのを
  // 防ぐため、AuthContext を更新せず children を描画しない。
  // (初回マウント時の null は _protected/route.tsx の beforeLoad が
  //  ログイン画面へ redirect するため、本ガードでは何もしない)
  if (!data && wasAuthenticatedRef.current) {
    return null
  }

  const isAuthenticated = !!data
  // 認証後のルートでのみで参照する想定なので、non-null assertion operator を使用してる
  const user = data!

  return (
    <AuthContext.Provider value={{ isAuthenticated, user }}>
      {children}
    </AuthContext.Provider>
  )
}
